import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BulkReference } from '@/api/bulk';
import { useInfiniteBulkReferences } from '@/hooks/useBulkCreation';
import { cn } from '@/lib/utils';
import {
  useBulkCreationStore,
  type BulkReferencePlacements,
} from '@/stores/bulkCreationStore';
import {
  DEFAULT_REFERENCE_SORT,
  DEFAULT_REFERENCE_PERIOD,
  ReferenceFilters,
  toReferencesFilter,
  type ReferenceSort,
  type ReferencePeriod,
} from './ReferenceFilters';
import { ReferenceList } from './ReferenceList';
import { ReferenceProductPanel } from './ReferenceProductPanel';

/** Delay between the last keystroke and the search request. */
export const REFERENCE_SEARCH_DEBOUNCE_MS = 300;

/** The store's placement per side; sides without a usable row stay absent. */
function toReferencePlacements(
  reference: BulkReference
): BulkReferencePlacements {
  const { front, back } = reference.fitReference;
  return {
    ...(front ? { front } : {}),
    ...(back ? { back } : {}),
  };
}

export interface ReferenceStepProps {
  className?: string;
}

/**
 * Step 01 of Bulk Creation (plan U8, R5–R13): the seller's published print
 * listings with search, publish-date chips and sort on the left, the product
 * choice of the selected reference on the right. Both columns are equally
 * wide from `lg` up and scroll on their own, so the product panel with its
 * colours and prices stays in view next to a long list; below `lg` they
 * stack. Filters are local UI state; the selection lives in the bulk store.
 */
export function ReferenceStep({ className }: ReferenceStepProps) {
  const { t } = useTranslation();
  const selectedId = useBulkCreationStore((state) => state.reference?.id);
  const selectReference = useBulkCreationStore(
    (state) => state.selectReference
  );

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [period, setPeriod] = useState<ReferencePeriod>(
    DEFAULT_REFERENCE_PERIOD
  );
  const [sort, setSort] = useState<ReferenceSort>(DEFAULT_REFERENCE_SORT);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search),
      REFERENCE_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [search]);

  const filter = useMemo(
    () => toReferencesFilter(debouncedSearch, period, sort),
    [debouncedSearch, period, sort]
  );

  const {
    data,
    isPending,
    isError,
    isPlaceholderData,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteBulkReferences(filter);

  const references = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.meta.total;

  const handleSelect = (reference: BulkReference) => {
    selectReference({
      id: reference.id,
      name: reference.name,
      products: reference.products.map((product) => ({
        productId: product.productId,
        name: product.name,
        hasBack: product.hasBack,
        disabledReason: product.disabledReason,
        colors: product.colors ?? [],
        activeColorCount: product.activeColorCount,
        pricing: product.pricing ?? null,
      })),
      referencePlacement: toReferencePlacements(reference),
      methodNames: reference.methods.map((method) => method.name),
      placement: reference.placement,
    });
  };

  const handleReset = () => {
    setSearch('');
    setDebouncedSearch('');
    setPeriod(DEFAULT_REFERENCE_PERIOD);
    setSort(DEFAULT_REFERENCE_SORT);
  };

  return (
    <section
      aria-labelledby="bulk-reference-title"
      className={cn('flex flex-col gap-6', className)}
    >
      <div className="flex flex-col gap-1.5">
        <h2 id="bulk-reference-title" className="dc-h2 text-xl text-ink">
          {t('seller.bulk.reference.title')}
        </h2>
        <p className="max-w-prose text-sm text-ink-2">
          {t('seller.bulk.reference.intro')}
        </p>
      </div>

      <div
        data-testid="bulk-reference-split"
        className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start"
      >
        <div
          data-testid="bulk-reference-column-list"
          className="flex min-w-0 flex-col gap-4 lg:max-h-[calc(100vh-17rem)] lg:min-h-[26rem] lg:overflow-y-auto lg:pr-1"
        >
          <ReferenceFilters
            search={search}
            onSearchChange={setSearch}
            period={period}
            onPeriodChange={setPeriod}
            sort={sort}
            onSortChange={setSort}
            total={isError && !data ? undefined : total}
          />
          <ReferenceList
            references={references}
            isPending={isPending}
            isError={isError && !data}
            isRefreshing={isPlaceholderData}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isFetchNextPageError={isFetchNextPageError}
            selectedId={selectedId ?? null}
            onSelect={handleSelect}
            onLoadMore={() => void fetchNextPage()}
            onRetry={() => void refetch()}
            onReset={handleReset}
          />
        </div>
        <div
          data-testid="bulk-reference-column-products"
          className="flex min-w-0 flex-col lg:max-h-[calc(100vh-17rem)] lg:min-h-[26rem] lg:overflow-y-auto lg:pr-1"
        >
          <ReferenceProductPanel />
        </div>
      </div>
    </section>
  );
}
